/**
 * Who else from support has a ticket open (DECISIONS #51). Fed by presence messages on the
 * live socket; nothing is stored on the server. Someone who stops sending (a closed laptop)
 * fades out: "typing" after a few seconds, being there at all after a minute or so.
 */

export type PresenceState = "viewing" | "typing";

export interface Present {
  id: string;
  name: string;
  state: PresenceState;
}

interface Entry {
  name: string;
  state: PresenceState;
  at: number;
}

// Browsers say they're still there every 20 seconds, so 50 seconds of silence means gone.
export const GONE_AFTER_MS = 50_000;
export const TYPING_FOR_MS = 6_000;

export class PresenceStore {
  private byTicket = new Map<string, Map<string, Entry>>();
  private listeners = new Set<() => void>();
  // useSyncExternalStore needs the same array back until something changes.
  private snapshots = new Map<string, Present[]>();

  constructor(private now: () => number = () => Date.now()) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Returns true when this person wasn't known to be here before (so we can say hello back). */
  update(ticket: string, user: { id: string; name: string }, state: PresenceState | "left"): boolean {
    const people = this.byTicket.get(ticket) ?? new Map<string, Entry>();
    const fresh = !people.has(user.id);
    if (state === "left") people.delete(user.id);
    else people.set(user.id, { name: user.name, state, at: this.now() });
    this.byTicket.set(ticket, people);
    this.changed(ticket);
    return fresh && state !== "left";
  }

  /** Drop people who've gone quiet, and stale "typing". Call every few seconds. */
  tick(): void {
    const now = this.now();
    for (const [ticket, people] of this.byTicket) {
      let changed = false;
      for (const [id, entry] of people) {
        if (now - entry.at > GONE_AFTER_MS) {
          people.delete(id);
          changed = true;
        } else if (entry.state === "typing" && now - entry.at > TYPING_FOR_MS) {
          entry.state = "viewing";
          changed = true;
        }
      }
      if (changed) this.changed(ticket);
    }
  }

  /** Forget everyone (the socket dropped: they'll say hello again when it's back). */
  clear(): void {
    const tickets = [...this.byTicket.keys()];
    this.byTicket.clear();
    for (const ticket of tickets) this.changed(ticket);
  }

  people(ticket: string): Present[] {
    let snapshot = this.snapshots.get(ticket);
    if (!snapshot) {
      const people = this.byTicket.get(ticket) ?? new Map<string, Entry>();
      snapshot = [...people].map(([id, e]) => ({ id, name: e.name, state: e.state }));
      snapshot.sort((a, b) => a.name.localeCompare(b.name));
      this.snapshots.set(ticket, snapshot);
    }
    return snapshot;
  }

  private changed(ticket: string): void {
    this.snapshots.delete(ticket);
    for (const listener of this.listeners) listener();
  }
}

/** "Leah Brandt is viewing", "Leah Brandt is replying…", "Leah Brandt and Dan Okafor are viewing". */
export function presenceText(people: Present[]): string {
  if (!people.length) return "";
  const typing = people.filter((p) => p.state === "typing");
  const names = (list: Present[]) =>
    list.length === 1
      ? (list[0]?.name ?? "")
      : list.length === 2
        ? `${list[0]?.name} and ${list[1]?.name}`
        : `${list[0]?.name} and ${list.length - 1} others`;
  if (typing.length) return `${names(typing)} ${typing.length === 1 ? "is" : "are"} writing…`;
  return `${names(people)} ${people.length === 1 ? "is" : "are"} also viewing`;
}
